import React from 'react';
import { AlertTriangle, RotateCw, ArrowLeft, Frown, CheckCircle2 } from 'lucide-react';
import CinematicLoader from './CinematicLoader';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

const GenerateStep = ({
    isLoading,
    progress,
    currentStatus,
    generationError,
    onRetry,
    onReset,
}) => {
    const statusItems = [
        'Fetching business information',
        'Analyzing brand identity',
        'Creating visual elements',
        'Rendering final video',
    ];

    if (isLoading) {
        return (
            <div className="flex flex-col items-center max-w-xl mx-auto py-8">
                <div className="w-full rounded-xl border bg-card p-10 shadow-sm overflow-hidden relative">
                    <div className="mb-8 flex flex-col items-center">
                        <CinematicLoader />
                        <div className="text-center mt-4 z-10 relative">
                            <p className="text-xl font-bold tracking-tight text-foreground">Generating your video</p>
                            <p className="text-sm text-muted-foreground mt-1">AI Director is rendering your scene</p>
                        </div>
                    </div>

                    <div className="w-full mb-6">
                        <Progress value={progress} className="h-2" />
                    </div>

                    <div className="w-full space-y-3">
                        {statusItems.map((item, index) => (
                            <div key={index} className="flex items-center gap-3">
                                {index < currentStatus ? (
                                    <CheckCircle2 className="h-4 w-4 shrink-0 text-teal" />
                                ) : (
                                    <div className={`h-2.5 w-2.5 rounded-full shrink-0 ${
                                        index === currentStatus
                                            ? 'bg-coral animate-pulse'
                                            : 'bg-muted'
                                    }`} />
                                )}
                                <span className={`text-sm ${index < currentStatus
                                    ? 'text-muted-foreground line-through'
                                    : index === currentStatus
                                        ? 'text-foreground font-medium'
                                        : 'text-muted-foreground'
                                    }`}>
                                    {item}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (generationError) {
        return (
            <div className="flex flex-col items-center max-w-lg mx-auto py-8">
                <div className="rounded-xl border bg-card p-8 shadow-sm text-center w-full">
                    <div className="flex justify-center mb-4">
                        <Frown className="size-12 text-muted-foreground" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                        {generationError.title}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                        {generationError.message || 'Something went wrong. Please try again.'}
                    </p>
                    {generationError.jobId && (
                        <p className="text-xs text-muted-foreground mb-6 font-mono">
                            Reference ID: {generationError.jobId}
                        </p>
                    )}
                    <div className="flex items-center justify-center gap-3">
                        <Button onClick={onRetry}>
                            <RotateCw className="h-4 w-4 mr-2" />
                            Retry Generation
                        </Button>
                        <Button variant="outline" onClick={onReset}>
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back to Search
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

export default GenerateStep;
